import type {
  ComputerAutomationHost,
  ComputerAutomationRequest,
  ComputerAutomationResponse,
  ComputerAutomationStreamEvent,
  DesktopComputerAutomationHostError,
} from "@praxa/contracts";
import { AsyncResult, Atom } from "effect/unstable/reactivity";

type AutomationStreamResult<E> = AsyncResult.AsyncResult<ComputerAutomationStreamEvent, E>;

export class ComputerAutomationHostFailure extends Error {
  override readonly name = "ComputerAutomationHostFailure";
  readonly _tag: string;
  readonly detail?: unknown;

  constructor(error: DesktopComputerAutomationHostError) {
    super(error.message);
    this._tag = error._tag;
    if (error.detail !== undefined) this.detail = error.detail;
  }
}

export function serializeComputerAutomationError(
  error: unknown,
): NonNullable<ComputerAutomationResponse["error"]> {
  if (error instanceof ComputerAutomationHostFailure) {
    return {
      _tag: error._tag,
      message: error.message,
      ...(error.detail === undefined ? {} : { detail: error.detail }),
    };
  }
  return {
    _tag: "ComputerAutomationExecutionError",
    message: "Computer control failed on this device.",
  };
}

export function createComputerAutomationRequestConsumerAtom<E>(options: {
  readonly requestsAtom: Atom.Atom<AutomationStreamResult<E>>;
  readonly clientId: ComputerAutomationHost["clientId"];
  readonly connectionAtom: Atom.Writable<ComputerAutomationStreamEvent["connectionId"] | null>;
  readonly requestHandlerAtom: Atom.Atom<{
    readonly handle: (request: ComputerAutomationRequest) => Promise<unknown>;
  }>;
  readonly respond: (response: ComputerAutomationResponse) => Promise<unknown>;
  readonly label: string;
}): Atom.Atom<void> {
  return Atom.make((get) => {
    get.mount(options.connectionAtom);
    get.mount(options.requestHandlerAtom);
    let disposed = false;
    let activeConnectionId: ComputerAutomationStreamEvent["connectionId"] | null = null;
    let connectionExplicitlyAnnounced = false;
    let reportedConnectionId: ComputerAutomationStreamEvent["connectionId"] | null = null;
    let requestsVersion = 0;

    const consume = (result: AutomationStreamResult<E>) => {
      if (!AsyncResult.isSuccess(result)) return;
      const event = result.value;
      if (event.type === "connected") {
        activeConnectionId = event.connectionId;
        connectionExplicitlyAnnounced = true;
      } else if (activeConnectionId === null) {
        activeConnectionId = event.connectionId;
      } else if (activeConnectionId !== event.connectionId) {
        if (connectionExplicitlyAnnounced) return;
        activeConnectionId = event.connectionId;
      }
      if (reportedConnectionId !== event.connectionId) {
        reportedConnectionId = event.connectionId;
        get.set(options.connectionAtom, event.connectionId);
      }
      if (event.type === "connected") return;

      const request = event.request;
      void get
        .once(options.requestHandlerAtom)
        .handle(request)
        .then(
          (value) =>
            options.respond({
              clientId: options.clientId,
              connectionId: event.connectionId,
              requestId: request.requestId,
              ok: true,
              ...(value === undefined ? {} : { result: value }),
            }),
          (error) =>
            options.respond({
              clientId: options.clientId,
              connectionId: event.connectionId,
              requestId: request.requestId,
              ok: false,
              error: serializeComputerAutomationError(error),
            }),
        );
    };

    get.addFinalizer(() => {
      disposed = true;
    });
    const initialRequest = get.once(options.requestsAtom);
    if (AsyncResult.isSuccess(initialRequest)) {
      activeConnectionId = initialRequest.value.connectionId;
      connectionExplicitlyAnnounced = initialRequest.value.type === "connected";
      if (initialRequest.value.type === "connected") {
        reportedConnectionId = initialRequest.value.connectionId;
        get.set(options.connectionAtom, initialRequest.value.connectionId);
      }
    }
    get.subscribe(options.requestsAtom, (result) => {
      requestsVersion += 1;
      consume(result);
    });
    queueMicrotask(() => {
      const initialConnectionWasSkipped =
        AsyncResult.isSuccess(initialRequest) &&
        initialRequest.value.connectionId === activeConnectionId &&
        initialRequest.value.connectionId !== reportedConnectionId;
      if (!disposed && (requestsVersion === 0 || initialConnectionWasSkipped)) {
        consume(initialRequest);
      }
    });
  }).pipe(Atom.setIdleTTL(0), Atom.withLabel(options.label));
}
